import React from 'react';
import JSONbig from "json-bigint";
import {OrderListItem, OrderGeneralInfo, OrderStatusDescription, OrderTypeDescription, OrderStatus, OrderFailReasonDescription, ForceActionType, GuaranteedEntity, OrderType} from "../../models/order";
import {OrderStatusIcon} from './OrderStatusIcon';
import {OrderPromoBadges} from './OrderPromoBadges';
import {OrderActions} from './OrderActions';
import {NotificationService} from "../../common/notification";
import {ordersListApi, orderGeneralInfoApi} from "./orderApi";
import Stack from "@mui/joy/Stack";
import Sheet from "@mui/joy/Sheet";
import Tooltip from "@mui/joy/Tooltip";
import DebounceInput from "../../components/debounceInput/DebounceInput";
import SearchIcon from "@mui/icons-material/Search";
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import HardwareIcon from '@mui/icons-material/Hardware';
import IconButton, {iconButtonClasses} from "@mui/joy/IconButton";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import InfoIcon from '@mui/icons-material/Info';
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import Typography from "@mui/joy/Typography";
import Divider from "@mui/joy/Divider";
import Button from "@mui/joy/Button";
import Box from "@mui/joy/Box";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import CircularProgress from '@mui/joy/CircularProgress';
import Table from "@mui/joy/Table";
import Link from "@mui/joy/Link";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemContent from "@mui/joy/ListItemContent";
import ListDivider from "@mui/joy/ListDivider";
import Avatar from "@mui/joy/Avatar";
import {getPages} from "../../common/pages/pages";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import { formatDateTime } from "../../common/dateTime";
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import {formatRubles} from "../../common/money";
import {Game} from "../../models/games";
import {gamesListApi} from "../games/gamesApi";
import Checkbox from '@mui/joy/Checkbox';
import Chip from '@mui/joy/Chip';
import ChipDelete from '@mui/joy/ChipDelete';
import StoreIcon from '@mui/icons-material/Store';  // Иконка продавца
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';    // Иконка покупателя
import SmartToyIcon from '@mui/icons-material/SmartToy';    // Иконка бота
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';    // Order Created
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';    // Items requested
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';    // Items received
import SendIcon from '@mui/icons-material/Send';    // Items sent
import DoneIcon from '@mui/icons-material/Done';    // Success
import CloseIcon from '@mui/icons-material/Close';  // Failure

type DefaultProps = {
    bidId?: number,
    bidderId?: bigint,
    status?: number,
    userId?: bigint,
    groupId?: number,
    // Начальные фильтры при переходе из аналитики
    failReason?: number,
    comment?: string,
    gameId?: number,
    from?: Date,
    to?: Date
}

const JSONbigNative = JSONbig({ useNativeBigInt: true });

export function OrdersGeneral(props: DefaultProps) {
    const {bidId, bidderId, status, userId, groupId, failReason: initialFailReason, comment: initialComment,
        gameId: initialGameId, from, to} = props;
    const [generalInfo, setGeneralInfo] = React.useState<OrderGeneralInfo>();
    const [orders, setOrders] = React.useState<OrderListItem[]>([]);
    const [games, setGames] = React.useState<Game[]>([]);
    const [isLoading, setLoading] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [forceModalOpen, setForceModalOpen] = React.useState(false);
    const [selectedOrderIds, setSelectedOrderIds] = React.useState<number[]>([]);
    const [orderIdsToForce, setOrderIdsToForce] = React.useState<number[]>([]);
    const [forceActionType, setForceActionType] = React.useState<ForceActionType>(ForceActionType.Handle);

    const [isTableLoading, setIsTableLoading] = React.useState(false);
    const [gamesAreLoading, setGamesAreLoading] = React.useState(false);
    const [page, setPage] = React.useState(1);
    const [pageCount, setPageCount] = React.useState(0);
    const [sellerId, setSellerId] = React.useState<bigint>(BigInt(-1));
    const [buyerId, setBuyerId] = React.useState<bigint>(bidderId || BigInt(-1));
    const [botId, setBotId] = React.useState(-1);
    const [gameId, setGameId] = React.useState(initialGameId ?? -1);
    const [bidFilterId, setBidFilterId] = React.useState<number>(bidId || -1);
    const [groupFilterId, setGroupFilterId] = React.useState<number>(groupId || -1);
    const [orderId, setOrderId] = React.useState(-1);
    // ?? а не ||: статус New = 0 — тоже фильтр
    const [orderStatus, setOrderStatus] = React.useState<number>(status ?? -1);
    const [failReasonFilter, setFailReasonFilter] = React.useState<number>(initialFailReason ?? -1);
    const [commentFilter, setCommentFilter] = React.useState<string>(initialComment ?? '');
    const [onlyWithPromoCode, setOnlyWithPromoCode] = React.useState(false);
    const [period, setPeriod] = React.useState<{from?: Date, to?: Date} | undefined>(
        from || to ? {from, to} : undefined);

    const isInitialMount = React.useRef(true);
    React.useEffect(() => {
        if (!open) {
            loadAllData().catch(console.error)
            .then(() => {isInitialMount.current = false;});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (isInitialMount.current) {
            return;
        }

        if (!open) {
            if (games.length === 0) {
                loadAllData().catch(console.error);
            }
            else {
                loadTableData().catch(console.error);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    React.useEffect(() => {
        if (isInitialMount.current) {
            return;
        }

        if (!open) {
            if (page !== 1) {
                setPage(1);
                return;
            }
            if (games.length === 0) {
                loadAllData().catch(console.error);
            }
            else {
                loadTableData().catch(console.error);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sellerId, buyerId, botId, bidFilterId, groupFilterId, orderStatus, orderId, gameId, failReasonFilter, commentFilter, period, onlyWithPromoCode]);

    const forceOrdersAmount = React.useMemo(
        () => orders.filter((e) => orderIdsToForce.some(id => e.orderId === id)).reduce((prev, current) => prev + current.price, 0),
        [orders, orderIdsToForce],
    );

    // Тип заказов, если все они одного типа, иначе undefined
    const getOrdersType = React.useCallback(
        (ids: number[]): OrderType | undefined => {
            const types = Array.from(new Set(orders.filter((o) => ids.includes(o.orderId)).map((o) => o.type)));
            return types.length === 1 ? types[0] : undefined;
        },
        [orders],
    );

    const forceOrderType = React.useMemo(
        () => getOrdersType(orderIdsToForce),
        [getOrdersType, orderIdsToForce],
    );

    const isOrderSelectable = (order: OrderListItem) => !OrderStatus.isCompleted(order.status);

    const selectableOrderIds = React.useMemo(
        () => Array.from(new Set(orders.filter((e) => !OrderStatus.isCompleted(e.status)).map((e) => e.orderId))),
        [orders],
    );

    const selectedHandleIds = React.useMemo(
        () => selectedOrderIds.filter((id) => orders.some((o) => o.orderId === id && !OrderStatus.isCompleted(o.status))),
        [selectedOrderIds, orders],
    );

    const selectedCompleteIds = React.useMemo(
        () => selectedOrderIds.filter((id) => orders.some((o) => o.orderId === id && o.status === OrderStatus.ManualMode)),
        [selectedOrderIds, orders],
    );

    const selectedCompleteIdsSameType = getOrdersType(selectedCompleteIds) !== undefined;

    const allSelectableSelected = selectableOrderIds.length > 0
        && selectableOrderIds.every((id) => selectedOrderIds.includes(id));
    const someSelectableSelected = selectableOrderIds.some((id) => selectedOrderIds.includes(id));

    const loadTableData = async () => {
        setIsTableLoading(true)
        try {
            let result = await ordersListApi(sellerId, buyerId, botId, orderId, orderStatus, bidFilterId, userId || BigInt(-1), page, 20, gameId, groupFilterId, {
                failReason: failReasonFilter > -1 ? failReasonFilter : undefined,
                commentContains: commentFilter.trim() || undefined,
                createdFrom: period?.from,
                createdTo: period?.to,
                onlyWithPromoCode
            });
            if (result.result) {
                const res = result.value!;
                setPageCount(Math.ceil(res.count / 20));
                setOrders(res.items)
                setSelectedOrderIds([]);
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setIsTableLoading(false)
        }
    }

    const loadAllData = async () => {
        setLoading(true)
        try {
            let result = await orderGeneralInfoApi();
            if (result.result) {
                setGeneralInfo(result.value!)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
            await loadGames();
            await loadTableData()

        } finally {
            setLoading(false)
        }
    }

    const loadGames = async () => {
        setGamesAreLoading(true)
        try {
            let result = await gamesListApi();
            if (result.result) {
                setGames(result.value!)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setGamesAreLoading(false)
        }
    }

    const applyFilter = async () => {
        setOpen(false)
        if (page !== 1) {
            setPage(1);
        }
        else {
            await loadTableData();
        }
    }

    const getDetails = (order: OrderListItem): React.ReactNode => {
        return (
            <>
                <Typography level="body-xs">Тип: {OrderTypeDescription[order.type]}</Typography>
                <Typography
                    level="body-xs">Комиссия: {formatRubles(order.commission)}
                </Typography>
                <Typography level="body-xs">Создан: {formatDateTime(order.createdAt)}</Typography>
                {order.requestedFromSellerAt &&
                    <Typography
                        level="body-xs">Запрос к продавцу: {formatDateTime(order.requestedFromSellerAt)}</Typography>
                }
                {order.receivedItemsAt &&
                    <Typography
                        level="body-xs">Предметы получены: {formatDateTime(order.receivedItemsAt)}</Typography>
                }
                {order.sentToBuyerAt &&
                    <Typography
                        level="body-xs">Запрос к покупателю: {formatDateTime(order.sentToBuyerAt)}</Typography>
                }
                {order.completedAt &&
                    <Typography
                        level="body-xs">Завершен: {formatDateTime(order.completedAt)}</Typography>
                }
                {order.failReason &&
                    <Typography level="body-xs">Ошибка: {OrderFailReasonDescription[order.failReason]}</Typography>
                }
                {order.comment &&
                    <Typography level="body-xs">Комментарий: {order.comment}</Typography>
                }
            </>
        );
    }

    const renderFilters = () => {
        return (
            <React.Fragment>
                {status !== undefined
                ? <></>
                : <FormControl size="sm">
                    <FormLabel>Статус</FormLabel>
                    <Select
                        size="sm"
                        placeholder="Активность"
                        slotProps={{button: {sx: {whiteSpace: 'nowrap'}}}}
                        value={orderStatus}
                        onChange={(_, value) => setOrderStatus(value ?? -1)}
                    >
                        <Option value={-1}>Все</Option>
                        {
                            Object.entries(OrderStatusDescription).map((entity) => <Option key={Number(entity[0])}
                                value={Number(entity[0])}>{entity[1]}</Option>)
                        }
                    </Select>
                </FormControl>}
                <FormControl size="sm" sx={{ minWidth: '130px !important' }}>
                    <FormLabel>Бот (Steam ID или ID)</FormLabel>
                    <DebounceInput
                        size="sm"
                        max-width="130px"
                        placeholder="Бот"
                        debounceTimeout={500}
                        defaultValue={botId > -1 ? botId.toString() : ''}
                        handleDebounce={(val) => setBotId(val ? Number(val) : -1)}
                        sx={{ maxWidth: '140px' }}
                    />
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>Продавец (Steam ID или ID)</FormLabel>
                    <DebounceInput
                        size="sm"
                        placeholder="Продавец"
                        debounceTimeout={500}
                        defaultValue={sellerId > -1 ? sellerId.toString() : ''}
                        handleDebounce={(val) => setSellerId(JSONbigNative.parse(val || '-1'))}
                    />
                </FormControl>
                {bidderId !== undefined
                ? <></>
                : <FormControl size="sm">
                    <FormLabel>Покупатель (Steam ID или ID)</FormLabel>
                    <DebounceInput
                        size="sm"
                        placeholder="Покупатель"
                        debounceTimeout={500}
                        defaultValue={buyerId > -1 ? buyerId.toString() : ''}
                        handleDebounce={(val) => setBuyerId(JSONbigNative.parse(val || '-1'))}
                    />
                </FormControl>}
                {bidId !== undefined
                ? <></>
                : <FormControl size="sm">
                    <FormLabel>Заявка #</FormLabel>
                    <DebounceInput
                        size="sm"
                        placeholder="Заявка"
                        debounceTimeout={500}
                        defaultValue={(bidId ?? -1) > -1 ? bidId!.toString() : ''}
                        handleDebounce={(val) => setBidFilterId(val ? Number(val) : -1)}
                    />
                </FormControl>}
                {groupId !== undefined
                ? <></>
                : <FormControl size="sm" sx={{ minWidth: '120px !important' }}>
                    <FormLabel>Группа #</FormLabel>
                    <DebounceInput
                        size="sm"
                        placeholder="Группа"
                        debounceTimeout={500}
                        defaultValue={(groupId ?? -1) > -1 ? groupId!.toString() : ''}
                        handleDebounce={(val) => setGroupFilterId(val ? Number(val) : -1)}
                        sx={{ maxWidth: '120px !important' }}
                    />
                </FormControl>}
                {gamesAreLoading
                ? <></>
                : <FormControl size="sm" sx={{ minWidth: '120px !important' }}>
                    <FormLabel>Игра</FormLabel>
                    <Select
                        size="sm"
                        slotProps={{button: {sx: {whiteSpace: 'nowrap'}}}}
                        value={gameId}
                        onChange={(_, value) => setGameId(value ?? -1)}
                        sx={{ minWidth: '120px !important' }}
                    >
                        <Option value={-1}>Все</Option>
                        {
                            games.map((g) => <Option key={g.id} value={g.id}>{g.name}</Option>)
                        }
                    </Select>
                </FormControl>}
                <FormControl size="sm" sx={{ minWidth: '180px !important' }}>
                    <FormLabel>Причина неуспеха</FormLabel>
                    <Select
                        size="sm"
                        slotProps={{button: {sx: {whiteSpace: 'nowrap'}}}}
                        value={failReasonFilter}
                        onChange={(_, value) => setFailReasonFilter(value ?? -1)}
                    >
                        <Option value={-1}>Все</Option>
                        {
                            Object.entries(OrderFailReasonDescription).map((entity) => <Option key={Number(entity[0])}
                                value={Number(entity[0])}>{entity[1]}</Option>)
                        }
                    </Select>
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>Текст ошибки (все слова)</FormLabel>
                    <DebounceInput
                        size="sm"
                        placeholder="Слова из комментария"
                        debounceTimeout={500}
                        defaultValue={commentFilter}
                        handleDebounce={(val) => setCommentFilter(val)}
                        slotProps={{input: {maxLength: 200}}}
                    />
                </FormControl>
                <FormControl size="sm" sx={{justifyContent: 'flex-end'}}>
                    <Checkbox
                        size="sm"
                        label="Только с промокодом"
                        checked={onlyWithPromoCode}
                        onChange={(e) => setOnlyWithPromoCode(e.target.checked)}
                    />
                </FormControl>
                {period
                ? <FormControl size="sm">
                    <FormLabel>Период создания</FormLabel>
                    <Chip
                        variant="soft"
                        endDecorator={<ChipDelete aria-label="Сбросить период" onDelete={() => setPeriod(undefined)}/>}
                    >
                        {formatDateTime(period.from)} — {formatDateTime(period.to)}
                    </Chip>
                </FormControl>
                : <></>}
            </React.Fragment>
        );
    }

    const getDefaultActionType = (ids: number[]): ForceActionType => {
        if (ids.length <= 0) {
            return ForceActionType.Complete;
        }

        const toRefund = ids.every((id) => orders.some((order) =>
            order.orderId === id &&
            order.status === OrderStatus.ManualMode &&
            GuaranteedEntity.isGuaranteed(order)
        ));

        return toRefund ? ForceActionType.Refund : ForceActionType.Complete;
    }

    const renderSelectAllCheckbox = () => (
        <Checkbox
            size="sm"
            checked={allSelectableSelected}
            indeterminate={someSelectableSelected && !allSelectableSelected}
            disabled={selectableOrderIds.length <= 0}
            onChange={(event) => setSelectedOrderIds(event.target.checked ? selectableOrderIds : [])}
        />
    );

    const renderBulkActionButtons = () => (
        <>
            <Tooltip title="Принудительный запуск выбранных">
                <span>
                    <IconButton
                        size="sm"
                        variant="outlined"
                        color={selectedHandleIds.length > 0 ? "warning" : "neutral"}
                        disabled={selectedHandleIds.length <= 0}
                        onClick={() => {
                            setOrderIdsToForce(selectedHandleIds);
                            setForceActionType(ForceActionType.Handle);
                            setForceModalOpen(true);
                        }}
                    >
                        <RocketLaunchIcon/>
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title={selectedCompleteIds.length > 0 && !selectedCompleteIdsSameType
                ? "Выбраны заказы разных типов"
                : "Завершить выбранные заказы"}>
                <span>
                    <IconButton
                        size="sm"
                        variant="outlined"
                        color={selectedCompleteIds.length > 0 && selectedCompleteIdsSameType ? "primary" : "neutral"}
                        disabled={selectedCompleteIds.length <= 0 || !selectedCompleteIdsSameType}
                        onClick={() => {
                            setOrderIdsToForce(selectedCompleteIds);
                            setForceActionType(getDefaultActionType(selectedCompleteIds));
                            setForceModalOpen(true);
                        }}
                    >
                        <HardwareIcon/>
                    </IconButton>
                </span>
            </Tooltip>
        </>
    );

    return (
        <React.Fragment>
            <OrderActions orderIds={orderIdsToForce}
                          orderType={forceOrderType}
                          totalAmount={forceOrdersAmount}
                          actionType={forceActionType}
                          modalOpen={forceModalOpen}
                          setModalOpen={setForceModalOpen}
                          onCompleted={(success: boolean, actionType: ForceActionType, successIds: number[], failedIds: number[]) => {
                            setOrderIdsToForce([]);
                            if (forceActionType === ForceActionType.Handle) {
                                return;
                            }

                            if (success && successIds.length > 0) {
                                loadTableData();
                            }}} />

            {isLoading ?
                <CircularProgress/>
                :
                <>
                    {(bidId === undefined && bidderId === undefined && status === undefined && userId === undefined)
                    ? <Box
                        sx={{
                            width: '100%',
                            maxWidth: 500,
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                            gap: 2,
                            my: 1
                        }}
                    >
                        <Card variant="outlined">
                            <CardContent>
                                <Typography level="title-md">В продаже</Typography>
                                <Typography>{generalInfo?.onSale}</Typography>
                            </CardContent>

                        </Card>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography level="title-md">Продано всего</Typography>
                                <Typography>{generalInfo?.sold}</Typography>
                            </CardContent>
                        </Card>
                    </Box>
                    : <></>}
                    <Sheet
                        className="SearchAndFilters-mobile"
                        sx={{display: {xs: 'flex', sm: 'none'}, my: 1, gap: 1}}
                    >
                        <DebounceInput
                            size="sm"
                            placeholder="Id ордера"
                            startDecorator={<SearchIcon/>}
                            sx={{flexGrow: 1}}
                            debounceTimeout={500}
                            defaultValue={orderId > 0 ? orderId.toString() : ''}
                            handleDebounce={(val) => setOrderId(Number(val))}
                        />
                        <IconButton
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            onClick={() => setOpen(true)}
                        >
                            <FilterAltIcon/>
                        </IconButton>
                        <Stack direction={'row'} spacing={1} alignItems={'center'}>
                            {renderSelectAllCheckbox()}
                            {renderBulkActionButtons()}
                        </Stack>

                        <Modal open={open} onClose={() => setOpen(false)}>
                            <ModalDialog aria-labelledby="filter-modal" layout="fullscreen">
                                <ModalClose/>
                                <Typography id="filter-modal" level="h2">
                                    Фильтры
                                </Typography>
                                <Divider sx={{my: 2}}/>
                                <Sheet sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                    {renderFilters()}
                                    <Button color="primary" onClick={applyFilter}>
                                        Применить
                                    </Button>
                                    <Button color="neutral" onClick={() => setOpen(false)}>
                                        Закрыть
                                    </Button>
                                </Sheet>
                            </ModalDialog>
                        </Modal>
                    </Sheet>
                    <Box
                        className="SearchAndFilters-tabletUp"
                        sx={{
                            borderRadius: 'sm',
                            py: 2,
                            display: {xs: 'none', sm: 'flex'},
                            flexWrap: 'wrap',
                            gap: 1.5,
                            '& > *': {
                                minWidth: {xs: '120px', md: '160px'},
                            },
                        }}
                    >
                        <FormControl size="sm">
                            <FormLabel>Id ордера</FormLabel>
                            <DebounceInput
                                size="sm"
                                placeholder="Order ID"
                                startDecorator={<SearchIcon/>}
                                debounceTimeout={500}
                                handleDebounce={(val) => setOrderId(Number(val))}
                            />
                        </FormControl>
                        {renderFilters()}
                        <Stack direction={'row'} spacing={1} alignItems={'end'} sx={{marginLeft: 'auto', minWidth: 'unset !important'}}>
                            {renderBulkActionButtons()}
                        </Stack>
                    </Box>
                    {isTableLoading ?
                        <CircularProgress/> :
                        <Sheet
                            className="OrderTableContainer"
                            sx={{
                                display: {xs: 'none', sm: 'initial'},
                                width: '100%',
                                borderRadius: 'sm',
                                flexShrink: 1,
                                overflow: 'auto',
                                minHeight: 0,
                            }}
                        >
                            <Table
                                aria-labelledby="tableTitle"
                                stickyHeader
                                hoverRow
                                sx={{
                                    '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                                    '--Table-headerUnderlineThickness': '1px',
                                    '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                                    '--TableCell-paddingY': '4px',
                                    '--TableCell-paddingX': '8px',
                                }}
                            >
                                <thead>
                                <tr>
                                    <th style={{width: 40, padding: '12px 6px'}}>{renderSelectAllCheckbox()}</th>
                                    <th style={{width: 140, padding: '12px 6px', whiteSpace: 'nowrap'}}>ID</th>
                                    <th style={{width: 150, padding: '12px 6px'}}>Время</th>
                                    <th style={{width: 150, padding: '12px 6px'}}>Скин</th>
                                    <th style={{width: 70, padding: '12px 6px'}}>Игра</th>
                                    <th style={{width: 220, padding: '12px 6px'}}>Продавец/Покупатель/Бот</th>
                                    <th style={{width: 100, padding: '12px 6px'}}>Заявка</th>
                                    <th style={{width: 100, padding: '12px 6px'}}>Группа</th>
                                    <th style={{width: 200, padding: '12px 6px'}}>Статус</th>
                                    <th style={{width: 100, padding: '12px 6px'}}>Цена</th>
                                    <th style={{width: 100, padding: '12px 6px'}}></th>
                                </tr>
                                </thead>
                                <tbody>
                                {orders.map((row) => (
                                    <tr key={row.id}>
                                        <td>
                                            {isOrderSelectable(row) ?
                                                <Checkbox checked={selectedOrderIds.some(e => e === row.orderId)}
                                                          onChange={(event) => setSelectedOrderIds(event.target.checked ?
                                                          [ ...selectedOrderIds, row.orderId] : selectedOrderIds.filter(e => e !== row.orderId)
                                                )}/>
                                            :<></>}
                                        </td>
                                        <td style={{whiteSpace: 'nowrap'}}>
                                            <Stack spacing={1} direction={'column'}>
                                                <Link
                                                    underline="hover"
                                                    href={`/app/orders/${row.orderId}`}
                                                    target="_blank"
                                                    level="body-xs"
                                                >
                                                    #{row.orderId} (item #{row.id})
                                                </Link>
                                                <Typography level="body-xs" fontStyle={'italic'}>Код - {row.orderNumber}</Typography>
                                            </Stack>
                                        </td>
                                        <td>
                                            <Stack spacing={1} direction={'column'}>
                                                <Stack
                                                    direction="row"
                                                    spacing='5px'
                                                    sx={{
                                                        justifyContent: "left",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <Tooltip title='Создан'>
                                                        <AddShoppingCartIcon/>
                                                    </Tooltip>
                                                    <Typography level="body-xs">{formatDateTime(row.createdAt)}</Typography>
                                                </Stack>
                                                {row.requestedFromSellerAt &&
                                                    <Stack
                                                        direction="row"
                                                        spacing='5px'
                                                        sx={{
                                                            justifyContent: "left",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <Tooltip title='Запрос к продавцу'>
                                                            <ShoppingCartCheckoutIcon/>
                                                        </Tooltip>
                                                        <Typography level="body-xs">{formatDateTime(row.requestedFromSellerAt)}</Typography>
                                                    </Stack>
                                                }
                                                {row.receivedItemsAt &&
                                                     <Stack
                                                        direction="row"
                                                        spacing='5px'
                                                        sx={{
                                                            justifyContent: "left",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <Tooltip title='Предметы получены'>
                                                            <ShoppingBasketIcon/>
                                                        </Tooltip>
                                                        <Typography level="body-xs">{formatDateTime(row.receivedItemsAt)}</Typography>
                                                    </Stack>
                                                }
                                                {row.sentToBuyerAt &&
                                                    <Stack
                                                        direction="row"
                                                        spacing='5px'
                                                        sx={{
                                                            justifyContent: "left",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <Tooltip title='Запрос к покупателю'>
                                                            <SendIcon/>
                                                        </Tooltip>
                                                        <Typography level="body-xs">{formatDateTime(row.sentToBuyerAt)}</Typography>
                                                    </Stack>
                                                }
                                                {row.completedAt &&
                                                    <Stack
                                                        direction="row"
                                                        spacing='5px'
                                                        sx={{
                                                            justifyContent: "left",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <Tooltip title='Завершено'>
                                                            { row.status === OrderStatus.Completed ? <DoneIcon/> : <CloseIcon/>}
                                                        </Tooltip>
                                                        <Typography level="body-xs">{formatDateTime(row.completedAt)}</Typography>
                                                    </Stack>
                                                }
                                            </Stack>
                                        </td>
                                        <td>
                                            <Tooltip title={row.skinName}>
                                                <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                                    <Avatar
                                                        variant="outlined"
                                                        size="sm"
                                                        src={row.image}
                                                    />
                                                    <Stack spacing={0.5} direction={'column'}>
                                                        <Link
                                                            underline="hover"
                                                            href={`https://steamcommunity.com/market/search?q=${row.skinName}`}
                                                            target="_blank"
                                                            level="body-xs"
                                                            sx={{
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: '2',
                                                                WebkitBoxOrient: 'vertical',
                                                            }}
                                                        >
                                                            {row.skinName}
                                                        </Link>
                                                        <Typography level="body-xs">#{row.assetId}</Typography>
                                                    </Stack>
                                                </Stack>
                                            </Tooltip>
                                        </td>
                                        <td>
                                            <Typography level="body-xs">{row.gameName}</Typography>
                                        </td>
                                        <td>
                                            <Stack spacing={1} direction={'column'}>
                                                <Stack
                                                    direction="row"
                                                    spacing='5px'
                                                    sx={{
                                                        justifyContent: "left",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <Tooltip title='Продавец'>
                                                        <StoreIcon/>
                                                    </Tooltip>
                                                    <Link
                                                        underline="hover"
                                                        href={`/app/users/${row.sellerId.toString()}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        level="body-xs"
                                                    >
                                                        {row.sellerId.toString()}
                                                    </Link>
                                                    <Link
                                                        underline="hover"
                                                        href={`https://steamcommunity.com/profiles/${row.sellerId.toString()}/`}
                                                        target="_blank"
                                                        level="body-xs"
                                                    >
                                                        (Steam)
                                                    </Link>
                                                </Stack>
                                                <Stack
                                                    direction="row"
                                                    spacing='5px'
                                                    sx={{
                                                        justifyContent: "left",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <Tooltip title='Покупатель'>
                                                        <TransferWithinAStationIcon/>
                                                    </Tooltip>
                                                    <Link
                                                        underline="hover"
                                                        href={`/app/users/${row.buyerId.toString()}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        level="body-xs"
                                                    >
                                                        {row.buyerId.toString()}
                                                    </Link>
                                                    <Link
                                                        underline="hover"
                                                        href={`https://steamcommunity.com/profiles/${row.buyerId.toString()}/`}
                                                        target="_blank"
                                                        level="body-xs"
                                                    >
                                                        (Steam)
                                                    </Link>
                                                </Stack>
                                                <Stack
                                                    direction="row"
                                                    spacing='5px'
                                                    sx={{
                                                        justifyContent: "left",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <Tooltip title='Бот'>
                                                        <SmartToyIcon/>
                                                    </Tooltip>
                                                    {row.botId ?
                                                        <>
                                                            <Link
                                                                underline="hover"
                                                                href={`/app/bots/${row.botId?.toString()}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                level="body-xs"
                                                            >
                                                                #{row.botId.toString()}
                                                            </Link>
                                                            <Link
                                                                underline="hover"
                                                                href={`https://steamcommunity.com/profiles/${row.botSteamId?.toString()}/`}
                                                                target="_blank"
                                                                level="body-xs"
                                                            >
                                                                (Steam)
                                                            </Link>
                                                        </> : '-' }
                                                </Stack>
                                            </Stack>
                                        </td>
                                        <td>{row.bidId ?
                                            <Link
                                                underline="hover"
                                                href={`/app/bids?bidId=${row.bidId}#list`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                level="body-xs"
                                            >
                                                #{row.bidId.toString()}
                                            </Link>
                                            : '-'}
                                        </td>
                                        <td>{row.orderGroupId ?
                                            <Typography level="body-xs">#{row.orderGroupId.toString()}</Typography>
                                            : '-'}
                                        </td>
                                        <td>
                                            <OrderStatusIcon status={row.status}/>
                                        </td>
                                        <td>
                                            <Typography level="body-xs">{formatRubles(row.price)}</Typography>
                                            <OrderPromoBadges promoCodes={row.promoCodes}/>
                                        </td>
                                        <td>
                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                                <Tooltip
                                                    placement="top-end"
                                                    variant="outlined"
                                                    arrow
                                                    title={getDetails(row)}>
                                                    <InfoIcon/>
                                                </Tooltip>
                                                {OrderStatus.isCompleted(row.status)
                                                ?<></>
                                                :<Tooltip title="Принудительный запуск">
                                                    <IconButton
                                                        size="sm"
                                                        variant="outlined"
                                                        color="warning"
                                                        onClick={() => {
                                                            setOrderIdsToForce([row.orderId]);
                                                            setForceActionType(ForceActionType.Handle);
                                                            setForceModalOpen(true);
                                                        }}
                                                    >
                                                        <RocketLaunchIcon/>
                                                    </IconButton>
                                                </Tooltip>}
                                                {row.status === OrderStatus.ManualMode
                                                ?<Tooltip title="Завершить заказ">
                                                    <IconButton
                                                        size="sm"
                                                        variant="outlined"
                                                        color="neutral"
                                                        onClick={() => {
                                                            setOrderIdsToForce([row.orderId]);
                                                            setForceActionType(getDefaultActionType([row.orderId]));
                                                            setForceModalOpen(true);
                                                        }}
                                                    >
                                                        <HardwareIcon/>
                                                    </IconButton>
                                                </Tooltip>
                                                :<></>}
                                            </Box>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </Sheet>
                    }
                    <Box
                        className="Pagination-laptopUp"
                        sx={{
                            pt: 2,
                            gap: 1,
                            [`& .${iconButtonClasses.root}`]: {borderRadius: '50%'},
                            display: {
                                xs: 'none',
                                md: 'flex',
                            },
                        }}
                    >
                        <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            startDecorator={<KeyboardArrowLeftIcon/>}
                            disabled={page === 1}
                            onClick={() => setPage(state => state - 1)}
                        >
                            Назад
                        </Button>

                        <Box sx={{flex: 1}}/>
                        {getPages(pageCount, page).map((p) => (
                            Number(p) ?
                            <IconButton
                                key={p}
                                size="sm"
                                variant='outlined'
                                color={page === Number(p) ? "primary" : "neutral"}
                                sx={page === Number(p) ? { border: '2px solid' } : {}}
                                onClick={() => setPage(Number(p))}
                            >
                                {p}
                            </IconButton>
                            : <>{p}</>
                        ))}
                        <Box sx={{flex: 1}}/>
                        <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            endDecorator={<KeyboardArrowRightIcon/>}
                            disabled={page === pageCount}
                            onClick={() => setPage(state => state + 1)}
                        >
                            Дальше
                        </Button>
                    </Box>

                    <Box sx={{display: {xs: 'block', sm: 'none'}}}>
                        {orders.map((listItem) => (
                            <List key={listItem.id} size="sm" sx={{'--ListItem-paddingX': 0}}>
                                <ListItem
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'start',
                                    }}
                                >
                                    <ListItemContent sx={{display: 'flex', gap: 2, alignItems: 'start'}}>
                                        <div>
                                            <OrderStatusIcon status={listItem.status}/><br/>
                                            <Link
                                                underline="hover"
                                                href={`/app/orders/${listItem.orderId}`}
                                                target="_blank"
                                                level="body-xs"
                                                sx={{fontWeight: 600}}
                                            >
                                                Заказ #{listItem.orderId} (item #{listItem.id})
                                            </Link>
                                            <Typography level="body-xs" fontStyle={'italic'}>Код - {listItem.orderNumber}</Typography>
                                            <Stack spacing={1} direction={'row'}>
                                                <Link
                                                    underline="hover"
                                                    href={`https://steamcommunity.com/market/search?q=${listItem.skinName}`}
                                                    target="_blank"
                                                    level="body-xs"
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: '2',
                                                        WebkitBoxOrient: 'vertical',
                                                    }}
                                                >
                                                    {listItem.skinName}
                                                </Link>
                                                <Typography level="body-xs">({listItem.assetId})</Typography>
                                            </Stack>
                                            <Typography level="body-xs">Игра: {listItem.gameName}</Typography>
                                            <Stack spacing={1} direction={'row'}>
                                                <Typography level="body-xs">Продавец:</Typography>
                                                <Link
                                                    underline="hover"
                                                    href={`/app/users/${listItem.sellerId.toString()}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    level="body-xs"
                                                >
                                                    {listItem.sellerId.toString()}
                                                </Link>
                                                <Link
                                                    underline="hover"
                                                    href={`https://steamcommunity.com/profiles/${listItem.sellerId.toString()}/`}
                                                    target="_blank"
                                                    level="body-xs"
                                                >
                                                    (Steam)
                                                </Link>
                                            </Stack>
                                            <Stack spacing={1} direction={'row'}>
                                                <Typography level="body-xs">Покупатель:</Typography>
                                                <Link
                                                    underline="hover"
                                                    href={`/app/users/${listItem.buyerId.toString()}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    level="body-xs"
                                                >
                                                    {listItem.buyerId.toString()}
                                                </Link>
                                                <Link
                                                    underline="hover"
                                                    href={`https://steamcommunity.com/profiles/${listItem.buyerId.toString()}/`}
                                                    target="_blank"
                                                    level="body-xs"
                                                >
                                                    (Steam)
                                                </Link>
                                            </Stack>
                                            {listItem.botId
                                            ?<Stack spacing={1} direction={'row'}>
                                                <Typography level="body-xs">Бот:</Typography>
                                                <Link
                                                    underline="hover"
                                                    href={`/app/bots/${listItem.botId?.toString()}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    level="body-xs"
                                                >
                                                    #{listItem.botId.toString()}
                                                </Link>
                                                <Link
                                                    underline="hover"
                                                    href={`https://steamcommunity.com/profiles/${listItem.botSteamId?.toString()}/`}
                                                    target="_blank"
                                                    level="body-xs"
                                                >
                                                    (Steam link)
                                                </Link>
                                            </Stack>
                                            :<></>
                                            }
                                            {listItem.bidId
                                            ? <Stack spacing={1} direction={'row'}>
                                                <Typography level="body-xs">Заявка:</Typography>
                                                <Link
                                                    underline="hover"
                                                    href={`/app/bids?bidId=${listItem.bidId}#list`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    level="body-xs"
                                                >
                                                    #{listItem.bidId.toString()}
                                                </Link>
                                            </Stack>
                                            : <></>
                                            }
                                            {listItem.orderGroupId
                                            ? <Typography level="body-xs">Группа: {listItem.orderGroupId?.toString()}</Typography>
                                            : <></>
                                            }
                                            <Typography
                                                level="body-xs">Цена {new Intl.NumberFormat().format(listItem.price / 100)} {listItem.currency}</Typography>
                                            <OrderPromoBadges promoCodes={listItem.promoCodes}/>
                                            {getDetails(listItem)}
                                            <Stack spacing={1} direction={'row'}>
                                                {OrderStatus.isCompleted(listItem.status)
                                                    ?<></>
                                                    :<Tooltip title="Принудительный запуск">
                                                        <IconButton
                                                            size="sm"
                                                            variant="outlined"
                                                            color="warning"
                                                            onClick={() => {
                                                                setOrderIdsToForce([listItem.orderId]);
                                                                setForceActionType(ForceActionType.Handle);
                                                                setForceModalOpen(true);
                                                            }}
                                                        >
                                                            <RocketLaunchIcon/>
                                                        </IconButton>
                                                    </Tooltip>}
                                                {listItem.status === OrderStatus.ManualMode
                                                ?<Tooltip title="Завершить заказ">
                                                    <IconButton
                                                        size="sm"
                                                        variant="outlined"
                                                        color="neutral"
                                                        onClick={() => {
                                                            setOrderIdsToForce([listItem.orderId]);
                                                            setForceActionType(getDefaultActionType([listItem.orderId]));
                                                            setForceModalOpen(true);
                                                        }}
                                                    >
                                                        <HardwareIcon/>
                                                    </IconButton>
                                                </Tooltip>
                                                :<></>}
                                            </Stack>
                                        </div>
                                    </ListItemContent>
                                    {listItem.image
                                    ? <Box component="img" src={listItem.image} sx={{ height: "30px", width: "auto" }} />
                                    : <></>}
                                    {isOrderSelectable(listItem) ?
                                        <Checkbox checked={selectedOrderIds.some(e => e === listItem.orderId)}
                                                    onChange={(event) => setSelectedOrderIds(event.target.checked ?
                                                    [ ...selectedOrderIds, listItem.orderId] : selectedOrderIds.filter(e => e !== listItem.orderId)
                                        )}/>
                                    :<></>}
                                </ListItem>
                                <ListDivider/>
                            </List>
                        ))}
                        <Box
                            className="Pagination-mobile"
                            sx={{display: {xs: 'flex', md: 'none'}, alignItems: 'center', py: 2}}
                        >
                            <IconButton
                                aria-label="previous page"
                                variant="outlined"
                                color="neutral"
                                size="sm"
                                disabled={page === 1}
                                onClick={() => setPage(state => state - 1)}
                            >
                                <KeyboardArrowLeftIcon/>
                            </IconButton>
                            <Typography level="body-sm" sx={{mx: 'auto'}}>
                                Страница {page} из {pageCount}
                            </Typography>
                            <IconButton
                                aria-label="next page"
                                variant="outlined"
                                color="neutral"
                                size="sm"
                                disabled={page === pageCount}
                                onClick={() => setPage(state => state + 1)}
                            >
                                <KeyboardArrowRightIcon/>
                            </IconButton>
                        </Box>
                    </Box>
                </>
            }
        </React.Fragment>
    );
}
